import type { BankItem } from "../../engine/bank-types";

/**
 * 人物弟子 — the same question gets a different answer because a different
 * person asked it. Until a student can tell 子路 from 冉有, half the book's
 * teaching moves look arbitrary.
 */
export const FIGURE_ITEMS: BankItem[] = [
  {
    id: "fg-zilu-1",
    type: "figure",
    refs: [292],
    concepts: ["jiaoxue"],
    figures: ["zilu", "ranyou"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "由性情差異解釋同問異答",
    stem: "「聞斯行諸？」子路問，孔子要他先問父兄；冉有問，孔子要他立刻去做。理由是？",
    context: "公西华曰：“由也问闻斯行诸，子曰‘有父兄在’；求也问闻斯行诸，子曰‘闻斯行之’。赤也惑，敢问。”",
    options: [
      { id: "a", text: "求也退故進之，由也兼人故退之" },
      { id: "b", text: "子路年長，責任較重，所以要慎重", why: "孔子給的理由是性情而非年齒：一個退縮，一個好勝。" },
      { id: "c", text: "冉有的家境不同，父兄不在", why: "章中無此線索，孔子的解釋也不涉家庭狀況。" },
      { id: "d", text: "孔子隨口而答，並無深意", why: "公西華一問，孔子立刻給出明確理由，可見是有意為之。" },
    ],
    answerId: "a",
    explanation: "公西華的困惑很合理——同一句話兩個答案。孔子的回答讓因材施教變得可驗證：不是隨機，是各補所缺。",
  },
  {
    id: "fg-zilu-2",
    type: "figure",
    refs: [99, 100, 236],
    concepts: ["junzi", "yili"],
    figures: ["zilu"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "孔子對子路的一貫姿態",
    stem: "孔子說「道不行，乘桴浮於海，從我者其由與」，子路聽了很高興，孔子接著說「由也好勇過我，無所取材」。這一來一往顯示？",
    options: [
      { id: "a", text: "既許其忠勇可託，又當即抑其好勇" },
      { id: "b", text: "孔子後悔選了子路，改口否定他", why: "「好勇過我」仍是承認其勇，只是指出他缺乏裁度。" },
      { id: "c", text: "孔子真的打算出海，子路願意同行", why: "「乘桴浮於海」是感嘆道不行的比喻，非實際計畫。" },
      { id: "d", text: "孔子在嘲笑子路聽不懂話", why: "語帶調侃但無貶意，全書對子路的信任處處可見。" },
    ],
    answerId: "a",
    explanation: "這是孔子對子路的典型模式：先給一句最高的信任，再補一句最直接的提醒。9.27 稱他不恥敝袍，隨即又說「何足以臧」——同一手法。",
  },
  {
    id: "fg-yanhui-1",
    type: "figure",
    refs: [25, 274],
    concepts: ["xue", "jiaoxue"],
    figures: ["yanhui"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "顏回的「不違如愚」如何被翻案",
    stem: "「吾與回言終日，不違，如愚。退而省其私，亦足以發，回也不愚。」孔子的判斷過程是？",
    options: [
      { id: "a", text: "先疑其愚，後由省其私而確認不愚" },
      { id: "b", text: "孔子一開始就知道顏回聰明，只是說反話", why: "「退而省其私」表明他確實作了觀察才改判。" },
      { id: "c", text: "顏回確實愚鈍，只是肯用功", why: "結語「回也不愚」是明確的否定。" },
      { id: "d", text: "顏回善於在老師面前掩飾", why: "「亦足以發」是稱許其能發揮，不是掩飾。" },
    ],
    answerId: "a",
    explanation: "這章正是 2.10「察其所安」的實例：課堂上的表現不足為憑，私下的行事才見真章。孔子連最信任的學生也是這樣驗證的。",
  },
  {
    id: "fg-yanhui-2",
    type: "figure",
    refs: [279, 281],
    concepts: ["li", "tianming"],
    figures: ["yanhui"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "極度悲痛中仍守分寸",
    stem: "顏淵死，孔子哭到「慟」，卻拒絕門人厚葬，也不肯賣車為之置槨。這兩件事並列說明？",
    options: [
      { id: "a", text: "情可極盡，而禮的分寸仍不因情破" },
      { id: "b", text: "孔子並不像表面那樣看重顏回", why: "「天喪予」「子哭之慟」是全書最重的哀痛表達。" },
      { id: "c", text: "孔子捨不得財物", why: "他的理由是「鯉也死，有棺而無槨」，講的是分際不是吝惜。" },
      { id: "d", text: "厚葬在當時是不合法的", why: "門人終究厚葬了，可見並非法禁，而是孔子的堅持。" },
    ],
    answerId: "a",
    explanation: "最能說明禮的一章：不是壓抑情感的規矩，而是在情感最洶湧時仍不失其分。他哭得失態，卻不肯讓喪儀越了本分。",
  },
  {
    id: "fg-zigong-1",
    type: "figure",
    refs: [405, 286],
    concepts: ["zhiren", "junzi"],
    figures: ["zigong"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "孔子對子貢的固定提醒",
    stem: "子貢喜歡比較人物（「師與商也孰賢」「方人」）。孔子的反應是？",
    options: [
      { id: "a", text: "賜也賢乎哉？夫我則不暇" },
      { id: "b", text: "詳細評比各弟子高下，滿足他的好奇", why: "11.16 他只說「過猶不及」，拒絕排名。" },
      { id: "c", text: "嚴厲斥責他不該議論同門", why: "語氣是調侃而非斥責。" },
      { id: "d", text: "稱讚他有識人之明", why: "他一再抑制子貢的這個習慣，不是稱讚。" },
    ],
    answerId: "a",
    explanation: "「我可沒這個閒工夫」——這句話極見機鋒。子貢極聰明，孔子從不否認他的判斷力，只是不斷把他的注意力從評人轉回修己。",
  },
  {
    id: "fg-zigong-2",
    type: "figure",
    refs: [537, 536],
    concepts: ["zhiren"],
    figures: ["zigong"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "子貢護師的方式",
    stem: "有人說「子貢賢於仲尼」，子貢以宮牆為喻回應。他的比喻要點是？",
    options: [
      { id: "a", text: "己牆及肩一望可見，夫子之牆數仞" },
      { id: "b", text: "自己的學問已經超過老師，但不便明說", why: "整段比喻正是否認這一點。" },
      { id: "c", text: "老師的學問淺顯易懂，人人可入", why: "「不得其門而入」正是說難以窺見。" },
      { id: "d", text: "評價老師是不敬的行為，不該討論", why: "他並未迴避，而是正面作了說明。" },
    ],
    answerId: "a",
    explanation: "這個比喻的高明在於：它既解釋了為什麼有人覺得子貢更強（他的牆矮，看得見），又完全沒有貶低自己。言語第一，名副其實。",
  },
  {
    id: "fg-zengzi-1",
    type: "figure",
    refs: [4, 195],
    concepts: ["shu", "ren"],
    figures: ["zengzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "曾子的修養方式",
    stem: "曾子「吾日三省吾身」，所省的三件事是？",
    options: [
      { id: "a", text: "為人謀、與朋友交、傳不習乎" },
      { id: "b", text: "事父母孝乎、事君忠乎、與友信乎", why: "三省的內容明確為謀、交、傳三事，未列事父母與事君。" },
      { id: "c", text: "言不過行乎、行不違禮乎、心不怨乎", why: "這三項並非本章文字，是仿其句式擬造的。" },
      { id: "d", text: "學而不思乎、思而不學乎、學而不習乎", why: "混入了 2.15 的內容。" },
    ],
    answerId: "a",
    explanation: "三項各對一種關係：受人之託、朋友往來、老師所授。曾子的特點是把修養變成可以逐條清點的日課——11.18 說他「魯」，這正是魯人的辦法。",
  },
  {
    id: "fg-zengzi-2",
    type: "figure",
    refs: [191, 192],
    concepts: ["li", "junzi"],
    figures: ["zengzi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "臨終仍在驗收",
    stem: "曾子病重時召門弟子「啟予足！啟予手！」並引《詩》「戰戰兢兢，如臨深淵，如履薄冰」。他要表達的是？",
    options: [
      { id: "a", text: "一生戒慎守身，至此才敢說可以放下" },
      { id: "b", text: "他擔心弟子照顧不周，要檢查身體", why: "引《詩》三句明示這是一生戒慎的收束，不是醫護要求。" },
      { id: "c", text: "要弟子藉此學習侍疾之禮", why: "章旨在自證一生守身無虧，並非傳授侍疾的儀節。" },
      { id: "d", text: "他後悔一生過於謹慎", why: "「而今而後，吾知免夫」是釋然，不是後悔。" },
    ],
    answerId: "a",
    explanation: "身體受之父母，守全它是孝的最後一項。曾子把一生的謹慎收在這一刻驗收——《論語》裡最動人的臨終場面之一。",
  },
  {
    id: "fg-zaiwo-1",
    type: "figure",
    refs: [102, 497],
    concepts: ["xiao", "li"],
    figures: ["zaiwo"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "宰我的角色功能",
    stem: "宰我在書中屢遭孔子責備（晝寢、短喪、問井有仁）。他在《論語》中的作用可以理解為？",
    options: [
      { id: "a", text: "他的難題逼出了孔子最深的回答" },
      { id: "b", text: "他是孔門最差的學生，記載他只為警示後人", why: "11.3 把他列於言語科之首，與子貢並列，可見受重視。" },
      { id: "c", text: "他其實是孔子最欣賞的學生", why: "「朽木不可雕」「予之不仁也」是全書最重的責語。" },
      { id: "d", text: "他的記載都是後人偽造", why: "無版本依據，且其問答風格前後一致。" },
    ],
    answerId: "a",
    explanation: "沒有宰我的「三年之喪，期已久矣」，就沒有孔子那句「於女安乎」——把禮的根據追到人心不忍的那一問。難纏的學生自有其貢獻。",
  },
  {
    id: "fg-ranyou-1",
    type: "figure",
    refs: [287, 463],
    concepts: ["zheng", "yili"],
    figures: ["ranyou"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "才幹與擔當的落差",
    stem: "冉有政事之才受孔子肯定，卻兩次因附從季氏遭嚴責。這條線索說明孔子看重什麼？",
    options: [
      { id: "a", text: "才幹不能抵銷該說不時沒有說不" },
      { id: "b", text: "孔子反對弟子出仕做官", why: "他自己求仕，也稱許子路、冉有可以從政。" },
      { id: "c", text: "孔子認為冉有能力不足", why: "6.8 明說「求也藝，於從政乎何有」。" },
      { id: "d", text: "孔子與季氏有私怨", why: "批評的落點始終是冉有的選擇與行為的性質。" },
    ],
    answerId: "a",
    explanation: "「非吾徒也，小子鳴鼓而攻之可也」——逐出師門的重話，針對的是一個他親自推薦出仕的學生。這說明才幹從不是免責條款。",
  },
  {
    id: "fg-zixia-1",
    type: "figure",
    refs: [133, 286],
    concepts: ["junzi", "xue"],
    figures: ["zixia"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "「君子儒」與「小人儒」",
    stem: "孔子告誡子夏「女為君子儒！無為小人儒！」結合 11.16 說子夏「不及」，這句話針對的可能是？",
    options: [
      { id: "a", text: "誡其拘於細節，勿把學問做成謀生之技" }, { id: "e", text: "誡其恃才傲物，勿以文學自矜於同門", why: "子夏之失在「不及」而非過，孔子未曾以驕矜責他。" },
      { id: "b", text: "他品行不端，需要警告", why: "全書未見子夏品行受責，《子張》篇還大量保存其言。" },
      { id: "c", text: "他才學不足，需要加倍努力", why: "他列文學科之首，才學非其短。" },
      { id: "d", text: "他不夠尊師，需要規勸", why: "3.8 中他的追問還得到「起予者商也」的稱許。" },
    ],
    answerId: "a",
    explanation: "君子儒與小人儒之別不在學問多寡，在為什麼而學。這與 14.24「古之學者為己，今之學者為人」是同一條線。",
  },
  {
    id: "fg-zizhang-1",
    type: "figure",
    refs: [342],
    concepts: ["zhiren", "junzi"],
    figures: ["zizhang"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "達與聞的分別",
    stem: "子張問「士何如斯可謂之達」，孔子先反問「爾所謂達者？」子張答後，孔子說「是聞也，非達也」。分別在哪？",
    options: [
      { id: "a", text: "聞在名聲，達在質直好義而行得通" },
      { id: "b", text: "聞是聽說，達是通達，只是詞義不同", why: "孔子作了實質區分，不是詞義辨析。" },
      { id: "c", text: "聞指小名氣，達指大名氣", why: "達不以名氣論，正是本章的要點。" },
      { id: "d", text: "聞在鄉里，達在邦國，範圍不同", why: "孔子說兩者「在邦必達／必聞」都可及於邦家，差別不在範圍。" },
    ],
    answerId: "a",
    explanation: "孔子先問「你說的達是什麼」——這一反問本身就是教學。子張說的是名聲，孔子點破：你要的其實是被人知道，不是行得通。",
  },
  {
    id: "fg-guanzhong-1",
    type: "figure",
    refs: [62, 393],
    concepts: ["ren", "li", "zheng"],
    figures: ["guanzhong"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "同一人兩種評價並存",
    stem: "孔子既說管仲「器小」「不知禮」，又說「如其仁！如其仁！」。這兩種評價如何並存？",
    options: [
      { id: "a", text: "分項而評：器度不足而功業許其仁" },
      { id: "b", text: "孔子改變了看法，後說推翻前說", why: "兩處分別針對不同方面，並無時間上的推翻關係。" },
      { id: "c", text: "「如其仁」是反話，仍是批評", why: "上下文是駁子貢、子路質疑管仲不死，語意明確為肯定。" },
      { id: "d", text: "兩章記載互相矛盾，必有一誤", why: "分項評價本是孔子論人的常態，不必疑其有誤。" },
    ],
    answerId: "a",
    explanation: "這是《論語》論人最成熟的一次示範：不因一項否定全人，也不因功業掩蓋缺失。5.15 評孔文子也是同一手法。",
  },
  {
    id: "fg-minzi-1",
    type: "figure",
    refs: [129, 275],
    concepts: ["xiao", "chushi"],
    figures: ["minziqian"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "閔子騫的孝與辭聘",
    stem: "「孝哉閔子騫！人不間於其父母昆弟之言。」這句稱許的特別之處是？",
    options: [
      { id: "a", text: "依據不在自述，而在外人挑不出話" },
      { id: "b", text: "他的父母兄弟從不批評他", why: "「人不間」指外人無從挑剔那些話，不是家人不批評。" },
      { id: "c", text: "他經常向人講述自己如何盡孝", why: "正好相反，稱許來自他人而非自述。" },
      { id: "d", text: "他的孝行被記載在史書中", why: "本章的證據是輿論，不是文獻。" },
    ],
    answerId: "a",
    explanation: "很有意思的證據標準：家人說你好不算數，要外人聽了也挑不出毛病才算。這與 13.24「鄉人皆好之，未可也」的謹慎是同一路。",
  },
  {
    id: "fg-boyi-1",
    type: "figure",
    refs: [165, 115],
    concepts: ["chushi", "ren"],
    figures: ["boyi", "zigong"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "子貢用側問探知孔子立場",
    stem: "冉有想知道孔子是否支持衛君，子貢入內問「伯夷、叔齊何人也？」得答「古之賢人也……求仁而得仁，又何怨」，出來便說「夫子不為也」。他的推理是？",
    options: [
      { id: "a", text: "許讓國者為賢，自不贊成爭位之君" },
      { id: "b", text: "孔子稱讚古人，就表示他厭惡當世的一切君主", why: "推論過寬；子貢的推理針對的是爭位這一具體情形。" },
      { id: "c", text: "孔子說「何怨」，表示他不願捲入紛爭", why: "「何怨」是說伯夷叔齊不悔其選擇，不是孔子表態迴避。" },
      { id: "d", text: "子貢其實沒有得到答案，只是猜測", why: "他的推論有明確依據，隨即斷言「夫子不為也」。" },
    ],
    answerId: "a",
    explanation: "全書最精彩的一次側面提問：不直接問敏感的現實立場，只問一樁古事，答案就出來了。這是「言語」科第一的實戰示範。",
  },
  {
    id: "fg-zilu-3",
    type: "figure",
    refs: [285, 288],
    concepts: ["jiaoxue", "zhiren"],
    figures: ["zilu"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "孔子維護學生的方式",
    stem: "孔子說「由之瑟奚為於丘之門？」門人因此不敬子路，孔子隨即說「由也升堂矣，未入於室也」。這一補充的作用是？",
    options: [
      { id: "a", text: "拉回分寸：已升堂，不容旁人輕看" },
      { id: "b", text: "進一步批評子路，強調他不夠格", why: "「升堂矣」是肯定其已達相當程度。" },
      { id: "c", text: "說明子路的琴藝確實很差", why: "重點在門人態度的糾正，不在琴藝評分。" },
      { id: "d", text: "轉移話題，避免尷尬", why: "他是直接針對門人的反應作出更正。" },
    ],
    answerId: "a",
    explanation: "老師的一句隨口批評，被學生們當成了定論。孔子立刻把話補完整——這既是護短，也是精確：升堂與入室是兩個不同的刻度。",
  },
  {
    id: "fg-ziyou-1",
    type: "figure",
    refs: [480, 525],
    concepts: ["li", "jiaoxue"],
    figures: ["ziyou", "zixia"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "子游與子夏的教法之爭",
    stem: "子游批評子夏門人「當洒掃應對進退則可矣，抑末也，本之則無」。子夏的回應要點是？",
    options: [
      { id: "a", text: "君子之道有先後，本末皆教而有序" },
      { id: "b", text: "承認自己教得不好，接受批評", why: "他明確反駁「言游過矣」。" },
      { id: "c", text: "認為洒掃應對就是全部，不必再談本", why: "他說的是次序問題，並未否認有本。" },
      { id: "d", text: "指責子游好高騖遠，不會教學生", why: "他的回應是論理而非人身攻擊。" },
    ],
    answerId: "a",
    explanation: "孔門內部的教法之爭被原樣保留下來，兩邊都有道理。這一章也提醒讀者：《論語》後幾篇記的是弟子們的爭論，不全是孔子的定論。",
  },
  {
    id: "fg-zhonggong-1",
    type: "figure",
    refs: [126, 121],
    concepts: ["zhiren", "ren"],
    figures: ["zhonggong"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "犂牛之子的比喻",
    stem: "「犂牛之子騂且角，雖欲勿用，山川其舍諸？」孔子用這個比喻說仲弓，意思是？",
    options: [
      { id: "a", text: "犢若騂且角仍堪用，出身不決定價值" },
      { id: "b", text: "仲弓資質平常，需要更加努力", why: "比喻的落點正是「其舍諸」，即不會被捨棄。" },
      { id: "c", text: "祭祀用牲有嚴格規定，不可隨意", why: "祭牲只是喻體，本意在論人。" },
      { id: "d", text: "仲弓應當先改善家世再談出仕", why: "與比喻的結論完全相反。" },
    ],
    answerId: "a",
    explanation: "仲弓父親身分微賤。孔子用祭祀選牲作比——看的是這頭牛本身合不合格，不是牠父親是耕牛還是什麼。這是「有教無類」在論人上的延伸。",
  },
  {
    id: "fg-fanchi-1",
    type: "figure",
    refs: [350, 344],
    concepts: ["zheng", "jiaoxue"],
    figures: ["fanchi"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "樊遲請學稼被斥的理由",
    stem: "樊遲請學稼、學圃，孔子答「吾不如老農」「吾不如老圃」，並在他出去後說「小人哉，樊須也」。孔子的理由是？",
    options: [
      { id: "a", text: "志在為政而求末技，上好禮義則民歸" },
      { id: "b", text: "孔子輕視農民，認為耕作低賤", why: "他答的是「吾不如老農」，承認專業在對方；批的是樊遲的志向所在。" },
      { id: "c", text: "孔子不懂農事，因此惱羞成怒", why: "「吾不如」是實話實說，隨後的說明是就為政立論。" },
      { id: "d", text: "樊遲問得不合禮，冒犯了老師", why: "章中未涉禮節問題。" },
    ],
    answerId: "a",
    explanation: "這一章今日讀來最扎耳，也最需要放回語境：孔門之學是為政之學，孔子責的是志向的錯置。同時也該承認，其中確有時代的等級意識。",
  },
  {
    id: "fg-kongzi-1",
    type: "figure",
    refs: [184, 152, 170],
    concepts: ["xue", "jiaoxue"],
    figures: ["kongzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "孔子的自我評價尺度",
    stem: "孔子自述時說「若聖與仁，則吾豈敢」「我非生而知之者」，他唯一不推辭的自許是？",
    options: [
      { id: "a", text: "好學，以及為之不厭、誨人不倦" },
      { id: "b", text: "「知天命」，因為他五十而知天命", why: "那是敘述進境，非自許之詞；且他仍以「畏天命」自處。" },
      { id: "c", text: "「述而不作」，因為他確實傳承了古制", why: "那是自我定位，語氣仍是自謙。" },
      { id: "d", text: "他從不自許任何事", why: "「好學」與「誨人不倦」是他明確承認的。" },
    ],
    answerId: "a",
    explanation: "他推掉聖與仁這類評價，只認下兩件仍在進行的事。這種自我評價方式本身就是教學：不要用身分自我定義，用你正在做的事。",
  },
  {
    id: "fg-zilu-4",
    type: "figure",
    refs: [509, 508],
    concepts: ["chushi", "li"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "子路對隱者的回應",
    stem: "荷蓧丈人譏孔子「四體不勤，五穀不分」，子路的回應是？",
    options: [
      { id: "a", text: "不仕無義；道之不行，已知之矣" },
      { id: "b", text: "與丈人辯論農事，證明自己懂稼穡", why: "他的回應是就出處之義立論，不涉農事。" },
      { id: "c", text: "默然不答，回去告訴孔子", why: "他確實回報孔子，但也留下了明確的答辭。" },
      { id: "d", text: "承認隱者說得對，勸孔子退隱", why: "他的立場正相反。" },
    ],
    answerId: "a",
    explanation: "「道之不行，已知之矣」——這句話與「知其不可而為之」互為表裡。子路平時粗率，這一次卻把孔門的立場說得最清楚。",
  },
];
